import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, History, FileSignature } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';
import {
  SUBMIT_META,
  CURRENT_STATUS_META,
  VERSION_TOOLTIP,
  fmt,
  deciderOf,
  prepareCycles,
} from './reviewHistoryShared';

/**
 * Full dedicated audit page for the review/attestation history of one
 * reviewable entity (FECOA-1611). This is the deep view behind the at-a-glance
 * modal: every submit-to-decision cycle rendered as a vertical timeline with
 * the complete record — decision reason and the full 21 CFR Part 11
 * e-signature block (meaning / signed-by / at).
 *
 * Route: /review-history/:entityType/:objectId
 * `entityName` and `currentStatus` arrive via router state when navigated from
 * the modal; the page still renders (without the header chrome) on a cold load.
 */

// One circular node sitting on the timeline rail, with its badge + body.
const TimelineNode = ({ meta, badgeExtra, topMargin, children }) => {
  const Icon = meta.icon;
  return (
    <li className={`ml-5 ${topMargin}`}>
      <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full border ${meta.cls}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}>
          {meta.label}
        </span>
        {badgeExtra}
      </div>
      {children}
    </li>
  );
};

const versionBadge = (version) => {
  if (version.kind === 'none') return null;
  if (version.kind === 'unchanged') {
    return (
      <span className="text-[11px] italic text-gray-400 dark:text-gray-500" title={VERSION_TOOLTIP}>
        Unchanged since last submission
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 cursor-help" title={VERSION_TOOLTIP}>
      <span className={`text-[11px] ${version.kind === 'reviewed' ? 'text-gray-500 dark:text-gray-400' : 'text-amber-600 dark:text-amber-400'}`}>
        {version.kind === 'reviewed' ? 'Reviewed version' : 'Content changed'}
      </span>
      <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-1.5 py-0.5">
        {version.hash}
      </span>
    </span>
  );
};

const ReviewHistoryPage = () => {
  const { entityType = 'assessment', objectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { entityName, currentStatus } = location.state || {};

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getReviewHistory(entityType, objectId);
        if (active) setRows(Array.isArray(data) ? data : data.results || []);
      } catch {
        if (active) setError('Could not load review history.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [entityType, objectId]);

  const cycles = prepareCycles(rows);
  const statusMeta = currentStatus ? CURRENT_STATUS_META[currentStatus] : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back</span>
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="bg-primary-50 dark:bg-primary-900/30 p-2.5 rounded-full flex-shrink-0">
            <History className="w-6 h-6 text-primary-600 dark:text-primary-300" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Audit history</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {entityName || 'Complete review and approval record'}
            </p>
          </div>
        </div>
        {statusMeta && (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border flex-shrink-0 ${statusMeta.cls}`}>
            {statusMeta.label}
          </span>
        )}
      </div>

      {/* States */}
      {loading && <div className="text-center py-16 text-gray-400 text-sm">Loading audit history…</div>}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}
      {!loading && !error && cycles.length === 0 && (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400 text-sm">
          No review activity yet for this item.
        </div>
      )}

      {/* Timeline */}
      {!loading && cycles.length > 0 && (
        <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2">
          {cycles.map(({ r, ci, decided, decisionMeta, version, standalone }) => {
            const decider = deciderOf(r);
            // Standalone actions (publish, revoke) render as a single node with
            // no preceding "Submitted for review" node (FECOA-1694).
            if (standalone) {
              return (
                <TimelineNode key={r.id} meta={decisionMeta} topMargin={ci === 0 ? 'mt-1' : 'mt-7'}>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    by <span className="font-medium">{decider.name}</span>
                    {decider.email && decider.email !== decider.name && (
                      <span className="text-gray-400"> ({decider.email})</span>
                    )}
                    <span className="text-gray-400"> · {fmt(r.decided_at || r.submitted_at)}</span>
                  </p>
                  {r.decision_reason && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-md px-3 py-2">
                      “{r.decision_reason}”
                    </p>
                  )}
                  {r.signed_by_name && (
                    <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                      <FileSignature className="w-3.5 h-3.5" />
                      {r.signature_meaning || 'Signed'}, {r.signed_by_name}
                      {r.signed_by_email && r.signed_by_email !== r.signed_by_name && (
                        <span className="text-gray-400"> ({r.signed_by_email})</span>
                      )}
                      <span className="text-gray-400"> · {fmt(r.signed_at)}</span>
                    </p>
                  )}
                </TimelineNode>
              );
            }
            return (
              <React.Fragment key={r.id}>
                {/* Submitted node — always present */}
                <TimelineNode
                  meta={SUBMIT_META}
                  badgeExtra={versionBadge(version)}
                  topMargin={ci === 0 ? 'mt-1' : 'mt-7'}
                >
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    by <span className="font-medium">{r.submitted_by_name || 'Unknown'}</span>
                    {r.submitted_by_email && r.submitted_by_email !== r.submitted_by_name && (
                      <span className="text-gray-400"> ({r.submitted_by_email})</span>
                    )}
                    <span className="text-gray-400"> · {fmt(r.submitted_at)}</span>
                  </p>
                </TimelineNode>

                {/* Decision node — the outcome, or an awaiting placeholder */}
                <TimelineNode meta={decisionMeta} topMargin="mt-4">
                  {decided ? (
                    <>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        by <span className="font-medium">{decider.name}</span>
                        {decider.email && decider.email !== decider.name && (
                          <span className="text-gray-400"> ({decider.email})</span>
                        )}
                        <span className="text-gray-400"> · {fmt(r.decided_at)}</span>
                      </p>
                      {r.decision_reason && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-md px-3 py-2">
                          “{r.decision_reason}”
                        </p>
                      )}
                      {r.signed_by_name && (
                        <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                          <FileSignature className="w-3.5 h-3.5" />
                          {r.signature_meaning || 'Signed'}, {r.signed_by_name}
                          {r.signed_by_email && r.signed_by_email !== r.signed_by_name && (
                            <span className="text-gray-400"> ({r.signed_by_email})</span>
                          )}
                          <span className="text-gray-400"> · {fmt(r.signed_at)}</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Waiting for an approver to review this submission.
                    </p>
                  )}
                </TimelineNode>
              </React.Fragment>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default ReviewHistoryPage;
